import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SalesChart = () => {
  const [chartType, setChartType] = useState('area');
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedPeriod, setSelectedPeriod] = useState('7D');

  const salesData = [
    { name: 'Mon', sales: 12400, orders: 45 },
    { name: 'Tue', sales: 15600, orders: 52 },
    { name: 'Wed', sales: 18200, orders: 61 },
    { name: 'Thu', sales: 14800, orders: 48 },
    { name: 'Fri', sales: 22100, orders: 73 },
    { name: 'Sat', sales: 28500, orders: 89 },
    { name: 'Sun', sales: 25300, orders: 82 }
  ];

  const timeRanges = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setTimeRange(period === '7D' ? '7d' : period === '30D' ? '30d' : period === '90D' ? '90d' : '1y');
  };

  const quickStats = [
    { label: 'Total Revenue', value: '$156,900', trend: 'up', change: '+12.5%' },
    { label: 'Orders', value: '1,245', trend: 'up', change: '+8.2%' },
    { label: 'Avg. Order Value', value: '$126.00', trend: 'down', change: '-2.1%' },
    { label: 'Conversion Rate', value: '3.4%', trend: 'up', change: '+0.3%' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1">
      <div className="p-6 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">Sales Analytics</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Revenue and order trends over time
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center bg-muted rounded-lg p-1">
              {['7D', '30D', '90D', '1Y']?.map((period) => (
                <Button
                  key={period}
                  variant={selectedPeriod === period ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handlePeriodChange(period)}
                  className="text-xs px-3 h-7"
                >
                  {period}
                </Button>
              ))}
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left" className="h-7 text-xs">
              Export
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {quickStats?.map((stat, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">{stat?.label}</p>
              <p className="text-base font-semibold text-foreground">{stat?.value}</p>
              <div className="flex items-center space-x-1 mt-1">
                <Icon 
                  name={stat?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                  size={12} 
                  className={stat?.trend === 'up' ? 'text-success' : 'text-error'}
                />
                <span className={`text-xs font-medium ${stat?.trend === 'up' ? 'text-success' : 'text-error'}`}>
                  {stat?.change}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-4 pb-4">
        <ResponsiveContainer width="100%" height={320}>
          {chartType === 'area' ? (
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="name" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="sales" 
                stroke="var(--color-primary)" 
                fill="var(--color-primary)"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </LineChart>
          ) : (
            <BarChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="name" 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Bar 
                dataKey="sales" 
                fill="var(--color-primary)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SalesChart;